import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  divIcon,
  LatLng, LatLngLiteral,
  Map as LeafletMap,
  Marker,
  marker,
  polyline,
} from "leaflet";
import { LorenColor } from "../../../loren-entity-form/components/loren-entity-form-field/constants/loren-color.constants";
import { LorenLocation } from "../../../../../features/loren-assets/interfaces/loren-location.interface";
import {getMapDefaults} from "../../../../../features/asset-map/map.defaults";

@Component({
    selector: "loren-base-map",
    templateUrl: "./loren-base-map.component.html",
    styleUrls: ["./loren-base-map.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenBaseMapComponent implements OnChanges {
  @Input() mapHeight = 200;
  @Input() location: LatLngLiteral;
  @Input() markerImage: string;
  @Input() tracePoints: LatLng[] = [];
  @Input() scrollWheelZoom = false;
  @Input() enableDrag = false;
  @Input() createMarkerOnClick = false;
  @Input() popupMessage: string;
  @Input() rounded: boolean;
  @Output() markerClick = new EventEmitter<void>();
  @Output() markerDragEnd = new EventEmitter<LorenLocation>();
  @Output() markerPositioned = new EventEmitter<LorenLocation>();

  options = getMapDefaults();

  private mapInstance: LeafletMap;
  private mapMarkers: Marker[] = [];
  private shouldZoomOutOnMarkerCreate = true;

  ngOnChanges(): void {
    this.options.scrollWheelZoom = this.scrollWheelZoom;
    this.clearAllMarkers();
    this.prepareMapMarkerPosition();
  }

  onMapReady(map: LeafletMap) {
    if (!map || !this.location) {
      return;
    }

    this.mapInstance = map;

    if (this.createMarkerOnClick) {
      this.mapInstance.on("click", (e) => {
        this.location.lat = e.latlng.lat;
        this.location.lng = e.latlng.lng;
        this.shouldZoomOutOnMarkerCreate = false;
        this.clearAllMarkers();
        this.prepareMapMarkerPosition();
        this.markerPositioned.emit({
          latitude: this.location.lat,
          longitude: this.location.lng,
        });
      });
    }

    // Remove the leaflet link
    this.prepareMapMarkerPosition();
    this.prepareMapTrace();
  }

  private prepareMapMarkerPosition() {
    if (!this.mapInstance) {
      return;
    }

    const latLngBounds = [];
    const mapMarker = this.createMarker();
    latLngBounds.push([this.location.lat, this.location.lng]);

    this.mapMarkers.push(mapMarker);

    this.mapInstance.addLayer(mapMarker);
    if (this.shouldZoomOutOnMarkerCreate) {
      this.mapInstance.setView(this.location, 15);
    }

    setTimeout(() => {
      this.mapInstance.invalidateSize();
    });
  }

  private createMarker() {
    const image = this.markerImage || "/icon-96x96.png";
    const icon = divIcon({
      className: "custom-div-icon",
      // html: "<div style='background-color:#c30b82;' class='marker-pin'></div><i class='material-icons'>weekend</i>",
      html: `<div class='marker-pin'></div><img src=${image}>`,
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

    const mapMarker = marker(this.location, {
      icon,
      draggable: this.enableDrag,
    });

    if (this.popupMessage) {
      mapMarker.bindPopup(`<h4>${this.popupMessage}</h4>`, {
        autoClose: false,
      });
    }

    mapMarker.on("click", () => {
      this.markerClick.emit();
    });

    if (this.enableDrag) {
      mapMarker.on("dragend", (e) => {
        const loc: LorenLocation = {
          longitude: e.target.getLatLng().lng,
          latitude: e.target.getLatLng().lat,
        };

        this.markerDragEnd.emit(loc);
      });
    }

    return mapMarker;
  }

  private clearAllMarkers() {
    this.mapMarkers.forEach((x) => {
      this.mapInstance.removeLayer(x);
    });

    this.mapMarkers = [];
  }

  private prepareMapTrace() {
    if (this.tracePoints.length === 0) {
      return;
    }

    const mapPolyline = polyline(this.tracePoints, {
      stroke: true,
      color: LorenColor[0].hexCode,
    });
    this.mapInstance.addLayer(mapPolyline);
  }
}
